import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useStateContext } from "../context/ContextProvider";
import { useDispatch, useSelector } from "react-redux";
import { getAllReviews } from "../redux/actions/reviews";

import {
  Loader,
  ReviewsCard,
} from "../components/utils";

import {
  motion,
  useInView,
} from "framer-motion";

import {
  Swiper,
  SwiperSlide,
} from "swiper/react";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import {
  Pagination,
  Navigation,
  Autoplay,
} from "swiper/modules";

import {
  FiChevronLeft,
  FiChevronRight,
  FiMessageCircle,
} from "react-icons/fi";

import {
  FaQuoteRight,
} from "react-icons/fa";

import { media } from "../server";

function Reviews() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { currentDir } = useStateContext();

  const {
    isLoading,
    AllReviews,
  } = useSelector((state) => state.Reviews);

  const sectionRef = useRef(null);

  const isInView = useInView(sectionRef, {
    once: true,
    amount: 0.15,
  });

  const isRTL = currentDir === "rtl";

  useEffect(() => {
    dispatch(getAllReviews(1, 12));
  }, [dispatch]);

  return (
    <section
      ref={sectionRef}
      className="
        relative
        overflow-hidden
        bg-back-secondary
        dark:bg-back-darksecondary
        py-24
        md:py-32
      "
    >
      {/* =========================
          Background Decorations
      ========================== */}

      <div
        className="
          absolute
          -top-52
          -end-52
          w-[500px]
          h-[500px]
          rounded-full
          border
          border-color-1/10
          pointer-events-none
        "
      />

      <div
        className="
          absolute
          top-20
          end-10
          w-[300px]
          h-[300px]
          rounded-full
          bg-color-1/10
          blur-[120px]
          pointer-events-none
        "
      />

      <div
        className="
          absolute
          -bottom-40
          -start-40
          w-[350px]
          h-[350px]
          rounded-full
          bg-color-2/5
          blur-[100px]
          pointer-events-none
        "
      />

      <div className="container relative z-10">

        {/* =========================
            Header
        ========================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 30,
          }}
          animate={
            isInView
              ? {
                  opacity: 1,
                  y: 0,
                }
              : {}
          }
          transition={{
            duration: 0.7,
          }}
          className="
            grid
            lg:grid-cols-[1fr_0.55fr]
            gap-10
            items-end
            mb-14
            md:mb-20
          "
        >
          <div>

            {/* Eyebrow */}

            <div
              className="
                inline-flex
                items-center
                gap-3
                mb-5
              "
            >
              <span
                className="
                  w-12
                  h-[2px]
                  rounded-full
                  bg-color-1
                "
              />

              <div
                className="
                  flex
                  items-center
                  gap-2
                  text-color-1
                "
              >
                <FiMessageCircle />

                <span
                  className="
                    font-kufi
                    text-sm
                  "
                >
                  {t("guest experiences")}
                </span>
              </div>
            </div>

            {/* Title */}

            <h2
              className="
                font-kufi
                font-bold
                text-tcolor-4
                dark:text-tcolor-1
                text-3xl
                leading-[1.5]
                md:text-5xl
                lg:text-[3.4rem]
                lg:leading-[1.4]
                max-w-3xl
                mb-6
              "
            >
              {t("what our guests say about Yaman")}
            </h2>

            <p
              className="
                font-kufi
                text-n-6
                dark:text-tcolor-2
                text-sm
                md:text-base
                leading-8
                max-w-2xl
              "
            >
              {t("Yaman reviews description")}
            </p>

          </div>

          {/* Large Quote */}

          <div
            className="
              hidden
              lg:flex
              justify-end
              items-end
            "
          >
            <FaQuoteRight
              className="
                text-[7rem]
                text-color-1/15
              "
            />
          </div>
        </motion.div>

        {/* =========================
            Reviews
        ========================== */}

        {isLoading ? (
          <div className="py-20">
            <Loader />
          </div>
        ) : AllReviews?.length > 0 ? (

          <div
            className="
              relative
              mx-auto
            "
          >

            {/* Navigation */}

            <div
              className="
                flex
                items-center
                justify-end
                gap-3
                mb-8
              "
            >
              <button
                className="
                  yaman-reviews-prev
                  flex
                  w-12
                  h-12
                  items-center
                  justify-center
                  rounded-full
                  border
                  border-color-1/25
                  bg-n-1
                  dark:bg-back-darkprimery
                  text-tcolor-4
                  dark:text-white
                  transition-all
                  duration-300
                  hover:bg-color-1
                  hover:text-white
                  hover:border-color-1
                "
                aria-label={t("previous reviews")}
              >
                {isRTL ? (
                  <FiChevronRight size={21} />
                ) : (
                  <FiChevronLeft size={21} />
                )}
              </button>

              <button
                className="
                  yaman-reviews-next
                  flex
                  w-12
                  h-12
                  items-center
                  justify-center
                  rounded-full
                  border
                  border-color-1/25
                  bg-n-1
                  dark:bg-back-darkprimery
                  text-tcolor-4
                  dark:text-white
                  transition-all
                  duration-300
                  hover:bg-color-1
                  hover:text-white
                  hover:border-color-1
                "
                aria-label={t("next reviews")}
              >
                {isRTL ? (
                  <FiChevronLeft size={21} />
                ) : (
                  <FiChevronRight size={21} />
                )}
              </button>
            </div>

            <Swiper
              key={currentDir}
              dir={currentDir}
              modules={[
                Pagination,
                Navigation,
                Autoplay,
              ]}
              slidesPerView={1}
              spaceBetween={18}
              speed={750}
              loop={AllReviews.length > 3}
              grabCursor={true}
              navigation={{
                prevEl: ".yaman-reviews-prev",
                nextEl: ".yaman-reviews-next",
              }}
              pagination={{
                clickable: true,
                dynamicBullets: true,
              }}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }}
              breakpoints={{
                640: {
                  slidesPerView: 1.2,
                  spaceBetween: 20,
                },

                768: {
                  slidesPerView: 2,
                  spaceBetween: 24,
                },

                1024: {
                  slidesPerView: 3,
                  spaceBetween: 26,
                },
              }}
              className="
                yaman-reviews-swiper
                !pb-14
                !overflow-visible
              "
            >
              {AllReviews.map((review, index) => (
                <SwiperSlide
                  key={review?._id || index}
                  className="!h-auto"
                >
                  <ReviewsCard
                    reviews={review}
                    index={index}
                  />
                </SwiperSlide>
              ))}
            </Swiper>

          </div>

        ) : (

          <div
            className="
              rounded-[2rem]
              border
              border-color-1/15
              bg-n-1
              dark:bg-back-darkprimery
              py-16
              px-6
              text-center
            "
          >
            <FiMessageCircle
              className="
                mx-auto
                text-4xl
                text-color-1
                mb-5
              "
            />

            <p
              className="
                font-kufi
                text-n-6
                dark:text-tcolor-2
              "
            >
              {t("reviews will appear here")}
            </p>
          </div>

        )}

        {/* =========================
            Guest Faces
        ========================== */}

        {AllReviews?.length > 0 && (
          <motion.div
            initial={{
              opacity: 0,
              y: 20,
            }}
            animate={
              isInView
                ? {
                    opacity: 1,
                    y: 0,
                  }
                : {}
            }
            transition={{
              delay: 0.35,
            }}
            className="
              mt-6
              flex
              flex-col
              sm:flex-row
              items-center
              justify-center
              gap-4
            "
          >
            <div className="flex -space-x-3 rtl:space-x-reverse">

              {AllReviews
                ?.slice(0, 5)
                .map((review, index) => (
                  <div
                    key={review?._id || index}
                    className="
                      w-11
                      h-11
                      rounded-full
                      border-[3px]
                      border-back-secondary
                      dark:border-back-darksecondary
                      overflow-hidden
                      bg-n-3
                    "
                  >
                    {review?.Image && (
                      <img
                        src={`${media}${review.Image}`}
                        alt={
                          currentDir === "ltr"
                            ? review?.titleEn
                            : review?.titleAr
                        }
                        className="
                          w-full
                          h-full
                          object-cover
                        "
                      />
                    )}
                  </div>
                ))}
            </div>

            <p
              className="
                font-kufi
                text-sm
                text-n-6
                dark:text-tcolor-2
              "
            >
              {t("real experiences from Yaman guests")}
            </p>
          </motion.div>
        )}

      </div>
    </section>
  );
}

export default Reviews;